import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trophy, Heart, Coins, ChevronLeft, ChevronRight, Flame } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { TipCreatorModal } from "./TipCreatorModal";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtTimeLeft = (endsAt) => {
  if (!endsAt) return "";
  const ms = new Date(endsAt).getTime() - Date.now();
  if (ms <= 0) return "Closing soon";
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return `${h}h ${m}m left`;
};

const REALM_LABELS = {
  marketing: "Marketing",
  designer:  "Designing",
  cgi:       "CGI",
  movie:     "Movie",
};

/**
 * Horizontal carousel rendered above the masonry feed when the weekend
 * leaderboard is active (Sat 00:00 → Sun 12:00). Each tile shows rank, media,
 * creator, engagement, and a Tip button.
 *
 * The optional `realm` prop narrows the leaderboard to a single creative
 * surface (marketing | designer | cgi | movie). Passing `""` / undefined
 * shows the unified leaderboard across all realms. Re-fetches whenever the
 * realm changes so the carousel stays in sync with the parent's realm chips.
 */
export const WeekendTop20Carousel = ({ onOpenPost, realm = "" }) => {
  const { token } = useAuth();
  const [active, setActive] = useState(false);
  const [endsAt, setEndsAt] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tipPost, setTipPost] = useState(null);
  const [tick, setTick] = useState(0);
  const scrollRef = useRef(null);

  /* eslint-disable react-hooks/exhaustive-deps, react-hooks/set-state-in-effect */
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const params = realm ? { realm } : {};
        const r = await axios.get(`${API_URL}/api/community/leaderboard`, {
          params,
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!alive) return;
        setActive(!!r.data.active);
        setEndsAt(r.data.ends_at);
        setPosts(r.data.posts || []);
      } catch (e) {
        // silent — feed still renders
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [realm]);

  // Live countdown
  useEffect(() => {
    if (!active) return undefined;
    const i = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(i);
  }, [active]);
  /* eslint-enable react-hooks/exhaustive-deps, react-hooks/set-state-in-effect */

  if (loading || !active || posts.length === 0) return null;

  const scrollBy = (dx) => {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: dx, behavior: "smooth" });
  };

  const handleTipSent = () => {
    toast.success("Tip on its way — they&rsquo;ll love this!");
  };

  return (
    <div
      className="relative bg-gradient-to-br from-amber-50 via-coral-500/5 to-amber-100 border-2 border-amber-300/50 rounded-3xl p-5 shadow-ambient"
      data-testid="weekend-top20-carousel"
      // bumping tick keeps the countdown re-rendered without an unused-var lint warning
      data-tick={tick}
    >
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-amber-400 to-coral-500 text-white flex items-center justify-center shadow-md">
            <Trophy className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-700 flex items-center gap-1">
              <Flame className="w-3 h-3" /> Weekend Top 20
              {realm && REALM_LABELS[realm] && (
                <span className="ml-1 px-1.5 py-0.5 rounded-full bg-fuchsia-100 text-fuchsia-700 text-[9px] font-black tracking-wider" data-testid="leaderboard-realm-badge">
                  {REALM_LABELS[realm].toUpperCase()}
                </span>
              )}
            </p>
            <h2 className="text-xl md:text-2xl font-black text-ink-950 leading-tight">
              {realm && REALM_LABELS[realm]
                ? `Top ${REALM_LABELS[realm].toLowerCase()} this weekend`
                : `This weekend's best creators`}
            </h2>
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-700">
            Tipping open
          </p>
          <p className="text-sm font-bold text-ink-950" data-testid="leaderboard-countdown">
            {fmtTimeLeft(endsAt)}
          </p>
        </div>
      </div>

      <div className="relative">
        <button
          onClick={() => scrollBy(-320)}
          className="hidden md:flex absolute -left-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white border border-ink-200 shadow-md items-center justify-center hover:bg-ink-50"
          data-testid="leaderboard-scroll-left"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <button
          onClick={() => scrollBy(320)}
          className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white border border-ink-200 shadow-md items-center justify-center hover:bg-ink-50"
          data-testid="leaderboard-scroll-right"
        >
          <ChevronRight className="w-4 h-4" />
        </button>

        <div
          ref={scrollRef}
          className="flex gap-3 overflow-x-auto snap-x scroll-smooth pb-2 -mx-1 px-1"
          style={{ scrollbarWidth: "thin" }}
        >
          {posts.map((p) => (
            <div
              key={p.id}
              className="snap-start shrink-0 w-[220px] bg-white rounded-2xl overflow-hidden border-2 border-amber-300/50 hover:border-amber-500 transition-all shadow-sm"
              data-testid={`leaderboard-tile-${p.id}`}
            >
              <button
                type="button"
                onClick={() => onOpenPost?.(p)}
                className="relative w-full aspect-square block bg-ink-100 overflow-hidden group"
              >
                {p.kind === "video" ? (
                  <video
                    src={p.media_url}
                    muted
                    playsInline
                    preload="metadata"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <img
                    src={p.media_url}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                )}
                <span
                  className={`absolute top-2 left-2 px-2.5 py-1 rounded-full text-xs font-black shadow-md ${
                    p.top_rank === 1
                      ? "bg-gradient-to-r from-yellow-300 to-amber-500 text-ink-950"
                      : p.top_rank === 2
                      ? "bg-gradient-to-r from-slate-200 to-slate-400 text-ink-950"
                      : p.top_rank === 3
                      ? "bg-gradient-to-r from-orange-300 to-amber-700 text-white"
                      : "bg-gradient-to-r from-amber-400 to-coral-500 text-white"
                  }`}
                >
                  #{p.top_rank}
                </span>
              </button>
              <div className="p-3 space-y-2">
                <p className="text-xs font-bold text-ink-950 truncate" title={p.user_name}>
                  {p.user_name}
                </p>
                <div className="flex items-center gap-2 text-[11px] text-ink-500">
                  <span className="inline-flex items-center gap-0.5">
                    <Heart className="w-3 h-3" /> {p.likes_count || 0}
                  </span>
                  <span>·</span>
                  <span>{p.comments_count || 0} comments</span>
                </div>
                <button
                  onClick={() => setTipPost(p)}
                  data-testid={`leaderboard-tip-${p.id}`}
                  className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-coral-500 text-white text-xs font-bold hover:bg-coral-600 transition-colors"
                >
                  <Coins className="w-3 h-3" />
                  Tip creator
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {tipPost && (
        <TipCreatorModal
          post={tipPost}
          onClose={() => setTipPost(null)}
          onSent={handleTipSent}
        />
      )}
    </div>
  );
};

export default WeekendTop20Carousel;
